/**
 * Makes PUT /update_actual_sets work with no signal, for EVERY caller at once.
 *
 * About ten places save sets (actual_sets.tsx autosave, the client view's
 * complete-flush, the inline list view, gym view, group results...). Wrapping
 * each would drift, so this sits in the shared axios instance instead. With the
 * flag on:
 *
 *   - every set save carries an Idempotency-Key;
 *   - with no connection, or while that set already has queued work, the save
 *     is queued in IndexedDB and the caller's promise resolves with a 202 that
 *     has the real 200's shape plus `queued: true`;
 *   - a save that fails with no response (signal died mid-request) is queued
 *     under the SAME key, because it may have committed, and the server
 *     answers a replayed key from its record instead of running it twice.
 *
 * Errors that carry a response (403, 422, 500) are NOT queued: the server
 * answered, and the caller's existing handling is the right one.
 *
 * Flag off: the interceptors pass everything through untouched.
 */

import axios, { AxiosError } from "axios";
import { isOfflineLoggingEnabled } from "./offline_flag";
import { isOnline } from "./connectivity";
import { isOfflineDbAvailable } from "./offline_db";
import {
  enqueueSetSave,
  hasPendingFor,
  isSetSaveRequest,
  mintIdempotencyKey,
  queuedSetSaveResponse,
} from "./set_save_queue";

export const IDEMPOTENCY_HEADER = "Idempotency-Key";

const readHeader = (headers, name) => {
  if (!headers) return undefined;
  if (typeof headers.get === "function") return headers.get(name) ?? undefined;
  return headers[name];
};

const writeHeader = (headers, name, value) => {
  if (typeof headers.set === "function") headers.set(name, value);
  else headers[name] = value;
};

const parseData = (data) => {
  if (typeof data !== "string") return data;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
};

const isCancel = (error) =>
  axios.isCancel?.(error) || error?.code === AxiosError.ERR_CANCELED;

const applies = (config) =>
  isOfflineLoggingEnabled() &&
  isOfflineDbAvailable() &&
  !config?.tkcOfflineReplay &&
  isSetSaveRequest(config);

/**
 * @param {object} options
 * @param {() => void} options.onQueued  nudge the sync engine (count + flush)
 */
export function installOfflineInterceptors(
  instance = axios,
  { onQueued = () => {} } = {},
) {
  const requestId = instance.interceptors.request.use((config) => {
    if (!applies(config)) return config;

    const data = parseData(config.data);
    if (!data || data.assigned_set_id == null) return config;

    config.headers = config.headers || {};
    let key = readHeader(config.headers, IDEMPOTENCY_HEADER);
    if (!key) {
      key = mintIdempotencyKey();
      writeHeader(config.headers, IDEMPOTENCY_HEADER, key);
    }

    if (!isOnline() || hasPendingFor(data)) {
      // Never touches the network: answer from the queue.
      config.tkcOfflineQueued = true;
      config.adapter = async (adapterConfig) => {
        try {
          await enqueueSetSave(data, { idempotencyKey: key });
        } catch {
          // Could not save it on the device either. Fail the way a dropped
          // connection always has, so the caller's error path runs.
          throw new AxiosError(
            "Network Error",
            AxiosError.ERR_NETWORK,
            adapterConfig,
          );
        }
        onQueued();
        return queuedSetSaveResponse(adapterConfig, data);
      };
    }
    return config;
  });

  const responseId = instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error?.config;
      if (
        !config ||
        config.tkcOfflineQueued || // already failed to queue once
        error.response ||
        isCancel(error) ||
        !applies(config)
      ) {
        throw error;
      }
      const data = parseData(config.data);
      if (!data || data.assigned_set_id == null) throw error;

      try {
        await enqueueSetSave(data, {
          idempotencyKey: readHeader(config.headers, IDEMPOTENCY_HEADER),
        });
      } catch {
        throw error;
      }
      onQueued();
      return queuedSetSaveResponse(config, data);
    },
  );

  return () => {
    instance.interceptors.request.eject(requestId);
    instance.interceptors.response.eject(responseId);
  };
}
